// Hook do auto-save pojedynczego klucza Settings z debounce 500ms.
// Reuzywa istniejacy batch endpoint usePutPanelSettings (1-elementowe objects).

import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { usePutPanelSettings } from 'api/endpoints/usePutPanelSettings';

export type FieldSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const DEBOUNCE_MS = 500;
const SAVED_DISPLAY_MS = 2000;

export interface IUsePartialSettingsSaveResult {
  save: (value: string) => void;
  status: FieldSaveStatus;
}

export const usePartialSettingsSave = (key: string): IUsePartialSettingsSaveResult => {
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [status, setStatus] = useState<FieldSaveStatus>('idle');
  const debouncedValue = useDebouncedValue(pendingValue, DEBOUNCE_MS);
  const { mutate } = usePutPanelSettings();
  const resetTimerRef = useRef<number | null>(null);
  const isFirstRunRef = useRef(true);
  // useRef na mutate - React Query gwarantuje stabilna referencja, ale trzymamy w ref
  // zeby NIE trafiala do deps useEffect (fragile - kazdy re-render mutation hooka moglby
  // wystartowac efekt z nieaktualnym debouncedValue).
  const mutateRef = useRef(mutate);
  useEffect(() => {
    mutateRef.current = mutate;
  }, [mutate]);

  useEffect(() => {
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      return;
    }

    if (debouncedValue === null) {
      return;
    }

    mutateRef.current(
      { objects: [{ key, value: debouncedValue, store_id: null }] },
      {
        onSuccess: () => {
          setStatus('saved');
          if (resetTimerRef.current !== null) {
            window.clearTimeout(resetTimerRef.current);
          }
          resetTimerRef.current = window.setTimeout(() => {
            setStatus('idle');
            resetTimerRef.current = null;
          }, SAVED_DISPLAY_MS);
        },
        onError: () => {
          setStatus('error');
        }
      }
    );
  }, [debouncedValue, key]);

  useEffect(
    () => () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
    },
    []
  );

  const save = (value: string): void => {
    setStatus('saving');
    setPendingValue(value);
  };

  return { save, status };
};
